import React from "react";
import { motion } from "framer-motion";
import { Quote } from "lucide-react";

interface Testimonial {
  name: string;
  role?: string;
  message: string;
  image?: string;
}

interface TestimonialCardProps {
  testimonial: Testimonial;
  width?: "full" | "compact";
  themeColor?: string;
}

const TestimonialCard: React.FC<TestimonialCardProps> = ({
  testimonial,
  width = "compact",
  themeColor = "#7C3AED", // Default purple if not provided
}) => {
  return (
    <motion.div
      className={`relative w-full flex flex-col items-center ${
        width === "full" ? "max-w-4xl" : "max-w-3xl"
      }`}
    >
      <h2 className="text-3xl font-bold mb-6 font-serif tracking-tight">
        Here's What Our Clients Say.
      </h2>

      {/* Card Container */}
      <div
        className="relative bg-white rounded-2xl shadow-lg px-6 sm:px-8 py-6 w-full border-2"
        style={{ borderColor: themeColor }}
      >
        {/* Pointer Arrow */}
        <div
          className="absolute -bottom-3 left-12 w-6 h-6 bg-white rotate-45 border-b-4 border-r-4"
          style={{ borderColor: themeColor }}
        ></div>

        {/* Quote Icon */}
        <div className="flex justify-start mb-4">
          <div
            className="p-2 rounded-lg shadow-sm"
            style={{ background: themeColor }}
          >
            <Quote className="text-white w-5 h-5" />
          </div>
        </div>

        {/* Message */}
        <p className="text-gray-700 italic leading-relaxed text-[15px] sm:text-base break-words whitespace-pre-wrap">
          “
          {testimonial.message.length > 250
            ? testimonial.message.slice(0, 250) + "..."
            : testimonial.message}
          ”
        </p>
      </div>

      {/* Footer (Name + Role) */}
      <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 mt-6 w-full justify-between">
        <div className="flex items-center gap-4">
          {testimonial.image ? (
            <img
              src={testimonial.image}
              alt={testimonial.name}
              className="w-14 h-14 rounded-full object-cover border-2 shadow-sm"
              style={{ borderColor: themeColor }}
            />
          ) : (
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center font-semibold text-lg shadow-sm"
              style={{
                backgroundColor: `${themeColor}`,
                color: themeColor,
              }}
            >
              {testimonial.name.charAt(0)}
            </div>
          )}

          <div>
            <p className="text-gray-900 font-semibold text-sm sm:text-base">
              {testimonial.name}
            </p>
            {testimonial.role && (
              <p className="text-gray-500 text-xs sm:text-sm">
                {testimonial.role}
              </p>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default TestimonialCard;
